import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Reveal } from "./Reveal";

const IMG = (id, w = 800) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${w}`;

const tiles = [
  {
    category: "Beverages",
    img: IMG(2983100),
    brands: ["Coca-Cola", "Pepsi", "Bru", "Tropicana", "Red Bull"],
    span: "md:col-span-5",
  },
  {
    category: "Snacks & Biscuits",
    img: IMG(568805),
    brands: ["Lay's", "Britannia", "Parle-G", "Haldiram's"],
    span: "md:col-span-4",
  },
  {
    category: "Dairy & Bakery",
    img: IMG(248412),
    brands: ["Amul", "Mother Dairy", "Nestlé"],
    span: "md:col-span-3",
  },
  {
    category: "Personal Care",
    img: IMG(1029896),
    brands: ["Dove", "Colgate", "Nivea", "Gillette"],
    span: "md:col-span-3",
  },
  {
    category: "Home & Cleaning",
    img: IMG(5217954),
    brands: ["Surf Excel", "Vim", "Harpic", "Good Knight"],
    span: "md:col-span-4",
  },
  {
    category: "Fresh & Staples",
    img: IMG(5677794),
    brands: ["Aashirvaad", "Tata Sampann", "Fortune", "Daawat"],
    span: "md:col-span-5",
  },
];

const companies = [
  "Hindustan Unilever",
  "ITC",
  "Nestlé",
  "Amul",
  "Britannia",
  "Dabur",
  "PepsiCo",
  "Coca-Cola",
  "Marico",
  "Godrej",
  "Tata Consumer",
  "Parle",
  "Mondelez",
  "Colgate-Palmolive",
  "Reckitt",
  "Emami",
];

const Tile = ({ t }) => (
  <motion.div
    data-testid={`brand-tile-${t.category.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    whileHover={{ y: -8 }}
    transition={{ type: "spring", stiffness: 300, damping: 22 }}
    className="group relative h-full min-h-[300px] w-full overflow-hidden rounded-2xl border border-brand-line"
  >
    <img
      src={t.img}
      alt={`${t.category} products available on Zeroomile`}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
    <div className="relative flex h-full flex-col justify-end p-7">
      <h3 className="font-display text-3xl uppercase tracking-tight text-white md:text-4xl">
        {t.category}
      </h3>
      <div className="mt-4 flex flex-wrap gap-2">
        {t.brands.map((b) => (
          <span
            key={b}
            className="rounded-full border border-white/20 bg-black/50 px-3 py-1 text-xs text-white backdrop-blur-md"
          >
            {b}
          </span>
        ))}
      </div>
    </div>
  </motion.div>
);

export const Brands = () => {
  return (
    <section
      id="brands"
      data-testid="brands-section"
      className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-32"
    >
      <div className="mb-14 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
        <Reveal>
          <p className="mb-6 text-xs uppercase tracking-[0.3em] text-brand-volt">Brands & Products</p>
          <h2 className="font-display text-6xl uppercase leading-[0.9] tracking-tight md:text-7xl">
            Stocked with the
            <br />
            brands you <span className="text-brand-volt">love</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="max-w-sm text-base text-brand-muted">
            From everyday staples to the latest launches — 3,000+ products from India&apos;s most
            trusted FMCG names, delivered from a Brightspot near you.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-9">
        {tiles.map((t, i) => (
          <Reveal key={t.category} className={t.span} y={50} delay={(i % 3) * 0.06}>
            <Tile t={t} />
          </Reveal>
        ))}
      </div>

      {/* FMCG company marquee */}
      <Reveal delay={0.1}>
        <p className="mb-6 mt-20 text-center text-xs uppercase tracking-[0.3em] text-brand-muted">
          Trusted FMCG partners on the network
        </p>
      </Reveal>
      <div className="border-y border-brand-line py-6">
        <Marquee speed={40} gradient={false} autoFill direction="right">
          {companies.map((c) => (
            <span
              key={c}
              className="mx-6 font-body text-xl font-medium tracking-tight text-brand-muted transition-colors duration-300 hover:text-brand-volt md:text-2xl"
            >
              {c}
              <span className="mx-6 text-brand-volt">&bull;</span>
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
};
